import React, { useState, useEffect } from 'react';
import { Loader } from 'lucide-react';
import { getAuthHeaders } from '../../context/AuthContext';
import CategorySelect from '../CategorySelect';
import SentimentSection from './SentimentSection';
import TagSection from './TagSection';
import ImageSection from './ImageSection';
import { BACKEND_URL } from '../../config/api';

const EMPTY_FORM = {
  name: '', category: '', brand: '', description: '', user_notes: '',
  image_url: '', thumbnail_url: '', colors: [], sentiments: [], tags: [],
};

/**
 * Shared Edit / Add Item Form
 *
 * mode = "edit": fetch/PUT existing item via /api/stash/{id}
 * mode = "create": no fetch; on submit, calls `onSubmit(formData)` and lets the
 *                  parent flow decide how to POST (so AI/URL flows can attach
 *                  their own metadata).
 *
 * Props:
 *   mode                     - "edit" | "create"  (default: "edit")
 *   item                     - existing item (edit mode only)
 *   initialFormData          - seed values (create mode)
 *   onSuccess                - called after successful save (edit mode) or after parent's onSubmit resolves
 *   onSubmit                 - (create mode) async (formData) => any. Parent does the POST.
 *   onCancel                 - cancel handler
 *   autoOpenChangeImage      - opens ChangeImageFlow on mount
 *   isFullScreen             - render in full-screen mobile layout vs modal layout
 *   submitLabel              - override the submit button label
 *   title                    - heading inline (optional; modal/page header still rendered by parent)
 */
const EditItemForm = ({
  mode = 'edit',
  item,
  initialFormData,
  onSuccess,
  onSubmit,
  onCancel,
  autoOpenChangeImage = false,
  isFullScreen = false,
  submitLabel,
}) => {
  const [formData, setFormData] = useState(() => ({ ...EMPTY_FORM, ...(initialFormData || {}) }));
  const [loading, setLoading] = useState(false);
  const [isAddingSentiment, setIsAddingSentiment] = useState(false);
  const [isAddingTag, setIsAddingTag] = useState(false);

  // Sync from `item` when it arrives async in edit mode.
  useEffect(() => {
    if (mode === 'edit' && item) {
      setFormData({
        name: item.name || '',
        category: item.category || '',
        brand: item.brand || '',
        description: item.description || '',
        user_notes: item.user_notes || '',
        image_url: item.image_url || '',
        thumbnail_url: item.thumbnail_url || '',
        colors: item.colors || [],
        sentiments: item.sentiments || [],
        tags: item.tags || [],
      });
    }
  }, [mode, item]);

  // In create mode, sync image-related fields from initialFormData whenever
  // they change. This is needed for AI/URL flows where the legacy
  // ProcessingDialog updates the parent's formData *after* this form has
  // already mounted with empty colors. Keep manual text fields locally
  // controlled so user edits aren't overwritten.
  const initImg = initialFormData?.image_url;
  const initThumb = initialFormData?.thumbnail_url;
  const initColorsKey = (initialFormData?.colors || []).join('|');
  const initSentimentsKey = (initialFormData?.sentiments || []).join('|');
  useEffect(() => {
    if (mode !== 'create' || !initialFormData) return;
    setFormData(prev => ({
      ...prev,
      image_url: initialFormData.image_url ?? prev.image_url,
      thumbnail_url: initialFormData.thumbnail_url ?? prev.thumbnail_url,
      colors: initialFormData.colors?.length ? initialFormData.colors : prev.colors,
      sentiments: initialFormData.sentiments?.length ? initialFormData.sentiments : prev.sentiments,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initImg, initThumb, initColorsKey, initSentimentsKey]);

  const patchForm = (patch) => setFormData(prev => ({ ...prev, ...patch }));

  const handleImageChange = (patch) => {
    // ImageSection emits { image_url?, thumbnail_url?, colors?, sentiments? }
    setFormData(prev => ({ ...prev, ...patch }));
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!formData.name?.trim()) { alert('Please enter a name'); return; }
    if (!formData.category) { alert('Please select a category'); return; }

    setLoading(true);
    try {
      if (mode === 'create') {
        await onSubmit?.(formData);
        // Parent owns success handling (e.g. close modal, refresh list)
      } else {
        const submitData = new FormData();
        Object.keys(formData).forEach(key => {
          if (key === 'colors') {
            submitData.append('colors_json', JSON.stringify(formData.colors));
          } else if (key === 'sentiments') {
            submitData.append('sentiments_json', JSON.stringify(formData.sentiments));
          } else if (key === 'tags') {
            submitData.append('tags_json', JSON.stringify(formData.tags));
          } else if (formData[key]) {
            submitData.append(key, formData[key]);
          }
        });
        const response = await fetch(`${BACKEND_URL}/api/stash/${item.item_id}`, {
          method: 'PUT', credentials: 'include', headers: getAuthHeaders(), body: submitData,
        });
        if (response.ok) {
          onSuccess?.();
        } else {
          const err = await response.clone().json();
          alert(err.detail || 'Failed to update item');
        }
      }
    } catch (e) {
      console.error(`${mode === 'create' ? 'Create' : 'Update'} item error:`, e);
      alert(`Failed to ${mode === 'create' ? 'add' : 'update'} item`);
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'edit' && !item) return null;

  const finalSubmitLabel = submitLabel || (mode === 'create' ? 'Add to Stash' : 'Save Changes');

  return (
    <div className={`flex flex-col ${isFullScreen ? 'min-h-screen bg-cream' : ''}`}>
      <div className={`flex-1 ${isFullScreen ? '' : 'overflow-y-auto'}`}>
        <div className={`${isFullScreen ? 'max-w-2xl mx-auto p-4' : 'p-4 sm:p-6'} space-y-6`}>

          {/* Image + Colors (shared) */}
          <ImageSection
            imageUrl={formData.image_url}
            colors={formData.colors}
            sentiments={formData.sentiments}
            onChange={handleImageChange}
            itemId={mode === 'edit' ? item?.item_id : undefined}
            itemName={formData.name}
            itemBrand={formData.brand}
            autoOpenChange={autoOpenChangeImage}
            bgWhite={isFullScreen}
          />

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">Name *</label>
            <input type="text" required value={formData.name}
              onChange={(e) => patchForm({ name: e.target.value })}
              placeholder="e.g., Navy Blue Cardstock"
              className={`w-full px-4 py-3 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 ${isFullScreen ? 'bg-white' : ''}`}
              style={{ fontSize: '16px' }}
              data-testid="item-name-input" />
          </div>

          {/* Category */}
          <div style={{ position: 'relative', zIndex: 50 }}>
            <label className="block text-sm font-medium text-charcoal mb-2">Category *</label>
            <CategorySelect
              value={formData.category}
              onChange={(e) => patchForm({ category: e.target.value })}
              data-testid="category-select" />
          </div>

          {/* Brand */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">Brand</label>
            <input type="text" value={formData.brand}
              onChange={(e) => patchForm({ brand: e.target.value })}
              placeholder="e.g., Simon Says Stamp"
              className={`w-full px-4 py-3 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 ${isFullScreen ? 'bg-white' : ''}`}
              style={{ fontSize: '16px' }}
              data-testid="brand-input" />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">Description</label>
            <textarea value={formData.description}
              onChange={(e) => patchForm({ description: e.target.value })}
              rows={3}
              placeholder="Optional description..."
              className={`w-full px-4 py-3 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 ${isFullScreen ? 'bg-white' : ''}`}
              style={{ fontSize: '16px' }}
              data-testid="description-textarea" />
          </div>

          {/* User Notes */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">Personal Notes</label>
            <textarea value={formData.user_notes}
              onChange={(e) => patchForm({ user_notes: e.target.value })}
              rows={2}
              placeholder="Add any personal notes about this item..."
              className={`w-full px-4 py-3 border border-beige rounded-lg focus:border-sage focus:ring-2 focus:ring-sage/20 ${isFullScreen ? 'bg-white' : ''}`}
              style={{ fontSize: '16px' }}
              data-testid="notes-textarea" />
          </div>

          {/* Sentiments */}
          <SentimentSection
            sentiments={formData.sentiments}
            onSentimentsChange={(sentiments) => patchForm({ sentiments })}
            imageUrl={formData.image_url || null}
            disabled={loading}
            onAddingStateChange={setIsAddingSentiment} />

          {/* Tags */}
          <TagSection
            tags={formData.tags}
            onTagsChange={(tags) => patchForm({ tags })}
            disabled={loading}
            onAddingStateChange={setIsAddingTag} />

          {/* Inline actions for full-screen mode */}
          {isFullScreen && (
            <div className="pt-4 pb-8 border-t border-beige">
              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={onCancel}
                  className="px-5 py-2.5 border border-beige text-charcoal rounded-lg hover:bg-cream disabled:opacity-50"
                  disabled={loading || isAddingSentiment || isAddingTag}
                  data-testid="cancel-btn">Cancel</button>
                <button type="button" onClick={handleSubmit}
                  className="px-5 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark flex items-center gap-2 disabled:opacity-50"
                  disabled={loading || isAddingSentiment || isAddingTag}
                  data-testid="submit-btn">
                  {loading
                    ? (<><Loader className="animate-spin" size={18} /><span>Saving...</span></>)
                    : (<span>{finalSubmitLabel}</span>)}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sticky footer for modal mode */}
      {!isFullScreen && (
        <div className="sticky bottom-0 bg-white border-t border-beige p-4 z-10">
          <div className="flex items-center justify-end gap-3">
            <button type="button" onClick={onCancel}
              className="px-5 py-2.5 border border-beige text-charcoal rounded-lg hover:bg-cream disabled:opacity-50"
              disabled={loading || isAddingSentiment || isAddingTag}
              data-testid="cancel-btn">Cancel</button>
            <button type="button" onClick={handleSubmit}
              className="px-5 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark flex items-center gap-2 disabled:opacity-50"
              disabled={loading || isAddingSentiment || isAddingTag}
              data-testid="submit-btn">
              {loading
                ? (<><Loader className="animate-spin" size={18} /><span>Saving...</span></>)
                : (<span>{finalSubmitLabel}</span>)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditItemForm;
